import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { server } from "../../server";
import { useSelector } from "react-redux";
import { AiOutlineSend } from "react-icons/ai";
import { TfiGallery } from "react-icons/tfi";
import socketIO from "socket.io-client";
import { format } from "timeago.js";
import { socketEndpoint } from "../../config/socket";
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
  readImageFileAsDataUrl,
} from "../../utils/imageUploadPreparation";

const socketId = socketIO(socketEndpoint, { transports: ["websocket"] });

const getMessageImageSrc = (image) => {
  if (!image) {
    return "";
  }

  return typeof image === "string" ? image : image.url;
};

const DashboardMessages = () => {
  const { seller,isLoading } = useSelector((state) => state.seller);
  const [conversations, setConversations] = useState([]);
  const [arrivalMessage, setArrivalMessage] = useState(null);
  const [currentChat, setCurrentChat] = useState();
  const [messages, setMessages] = useState([]);
  const [userData, setUserData] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [activeStatus, setActiveStatus] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const handleIncomingMessage = (data) => {
      setArrivalMessage({
        sender: data.senderId,
        text: data.text,
        createdAt: Date.now(),
      });
    };

    socketId.on("getMessage", handleIncomingMessage);

    return () => {
      socketId.off("getMessage", handleIncomingMessage);
    };
  }, []);

  useEffect(() => {
    arrivalMessage &&
      currentChat?.members.includes(arrivalMessage.sender) &&
      setMessages((prev) => [...prev, arrivalMessage]);
  }, [arrivalMessage, currentChat]);

  useEffect(() => {
    const getConversation = async () => {
      if (!seller?._id) {
        return;
      }

      try {
        const response = await axios.get(
          `${server}/conversation/get-all-conversation-seller/${seller?._id}`,
          {
            withCredentials: true,
          }
        );

        setConversations(response.data.conversations);
      } catch (error) {
        console.error(error);
      }
    };
    getConversation();
  }, [seller?._id, messages]);

  useEffect(() => {
    if (seller) {
      const sellerId = seller?._id;
      socketId.emit("addUser", sellerId);
      const handleOnlineUsers = (data) => {
        setOnlineUsers(data);
      };
      socketId.on("getUsers", handleOnlineUsers);

      return () => {
        socketId.off("getUsers", handleOnlineUsers);
      };
    }
  }, [seller]);

  const onlineCheck = (chat) => {
    const chatMembers = chat.members.find((member) => member !== seller?._id);
    const online = onlineUsers.find((user) => user.userId === chatMembers);

    return online ? true : false;
  };

  // get messages
  useEffect(() => {
    const getMessage = async () => {
      if (!currentChat?._id) {
        setMessages([]);
        return;
      }

      try {
        const response = await axios.get(
          `${server}/message/get-all-messages/${currentChat?._id}`
        );
        setMessages(response.data.messages);
      } catch (error) {
        console.error(error);
      }
    };
    getMessage();
  }, [currentChat]);

  const selectConversation = ({ chat, contact, online }) => {
    setCurrentChat(chat);
    setUserData(contact);
    setActiveStatus(online);
  };

  // create new message
  const sendMessageHandler = async (e) => {
    e.preventDefault();

    if (!seller?._id || !currentChat?._id) {
      return;
    }

    const message = {
      sender: seller._id,
      text: newMessage,
      conversationId: currentChat._id,
    };

    const receiverId = currentChat.members.find(
      (member) => member !== seller._id
    );

    socketId.emit("sendMessage", {
      senderId: seller._id,
      receiverId,
      text: newMessage,
    });

    try {
      if (newMessage !== "") {
        await axios
          .post(`${server}/message/create-new-message`, message)
          .then((res) => {
            setMessages((prevMessages) => [...prevMessages, res.data.message]);
            updateLastMessage();
          })
          .catch((error) => {
            console.error(error);
          });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const updateLastMessage = async () => {
    if (!seller?._id || !currentChat?._id) {
      return;
    }

    socketId.emit("updateLastMessage", {
      lastMessage: newMessage,
      lastMessageId: seller._id,
    });

    await axios
      .put(`${server}/conversation/update-last-message/${currentChat._id}`, {
        lastMessage: newMessage,
        lastMessageId: seller._id,
      })
      .then((res) => {
        setNewMessage("");
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) {
      return;
    }

    try {
      const preparedFile = await prepareImageFileForUpload(file);
      const imageDataUrl = await readImageFileAsDataUrl(preparedFile);
      imageSendingHandler(imageDataUrl);
    } catch (error) {
      console.error("Error preparing message image:", error);
      alert(getImagePreparationErrorMessage(error));
    }
  };

  const imageSendingHandler = async (image) => {
    if (!seller?._id || !currentChat?._id) {
      return;
    }

    const receiverId = currentChat.members.find(
      (member) => member !== seller._id
    );

    socketId.emit("sendMessage", {
      senderId: seller._id,
      receiverId,
      images: image,
    });

    try {
      await axios
        .post(`${server}/message/create-new-message`, {
          images: image,
          sender: seller._id,
          text: newMessage,
          conversationId: currentChat._id,
        })
        .then((res) => {
          setMessages((prevMessages) => [...prevMessages, res.data.message]);
          updateLastMessageForImage();
        });
    } catch (error) {
      console.error(error);
    }
  };

  const updateLastMessageForImage = async () => {
    if (!currentChat?._id || !seller?._id) {
      return;
    }

    await axios.put(
      `${server}/conversation/update-last-message/${currentChat._id}`,
      {
        lastMessage: "Photo",
        lastMessageId: seller._id,
      }
    );
  };

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const renderConversationList = () => (
    <div className="user-inbox-component-conversation-list shop-dashboard-conversation-list">
      {conversations && conversations.length > 0 ? (
        conversations.map((item, index) => (
          <MessageList
            data={item}
            key={item._id || index}
            currentChatId={currentChat?._id}
            selectConversation={selectConversation}
            me={seller?._id}
            online={onlineCheck(item)}
            isLoading={isLoading}
          />
        ))
      ) : (
        <div className="profile-settings-empty-state user-inbox-empty-state">
          <h3>No shop messages yet</h3>
          <p>Customer and member conversations for this shop profile will appear here.</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="shop-dashboard-messages-panel user-inbox-component-container user-inbox-component-container--embedded">
      <div className="user-inbox-component-workspace shop-dashboard-inbox-workspace">
        <aside className="user-inbox-component-contacts-panel shop-dashboard-inbox-contacts-panel">
          <header className="user-inbox-component-panel-header">
            <p>Shop Inbox</p>
            <h1 className="user-inbox-component-title">Contacts & Messages</h1>
            <span>{conversations.length} conversation{conversations.length === 1 ? "" : "s"}</span>
          </header>
          {renderConversationList()}
        </aside>

        <section className="user-inbox-component-thread-panel shop-dashboard-inbox-thread-panel">
          {currentChat ? (
            <SellerInbox
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              sendMessageHandler={sendMessageHandler}
              messages={messages}
              sellerId={seller?._id}
              userData={userData}
              activeStatus={activeStatus}
              scrollRef={scrollRef}
              handleImageUpload={handleImageUpload}
            />
          ) : (
            <div className="user-inbox-component-thread-empty">
              <p>Messages</p>
              <h3>Select a conversation</h3>
              <span>Open a contact to review the full message history, send replies, and share images from your shop.</span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const MessageList = ({
  data,
  currentChatId,
  selectConversation,
  me,
  online,
  isLoading
}) => {
  const [user, setUser] = useState(null);
  const handleClick = () => {
    selectConversation({ chat: data, contact: user, online });
  };

  useEffect(() => {
    const userId = data.members.find((member) => member !== me);

    if (!userId) {
      return;
    }

    const getUser = async () => {
      try {
        const res = await axios.get(`${server}/user/user-info/${userId}`);
        setUser(res.data.user);
      } catch (error) {
        console.error(error);
      }
    };
    getUser();
  }, [me, data]);

  const contactName = user?.name || "Contact";
  const lastMessageLabel = data?.lastMessageId === me ? "You" : contactName.split(" ")[0];

  return (
    <div
      className={`user-inbox-component-message-item shop-dashboard-message-list-item ${currentChatId === data?._id ? "active" : ""}`}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="user-inbox-component-avatar shop-dashboard-message-avatar-wrap">
        <img
          src={user?.avatar?.url || "/DefaultAvatar.png"}
          alt={contactName}
          className="user-inbox-component-avatar-img shop-dashboard-message-avatar"
        />
        {online ? (
          <div className="user-inbox-component-online-indicator" />
        ) : (
          <div className="user-inbox-component-offline-indicator" />
        )}
      </div>
      <div className="user-inbox-component-message-content shop-dashboard-message-list-copy">
        <div className="user-inbox-component-message-meta">
          <h1 className="user-inbox-component-message-username">{contactName}</h1>
          <span>{online ? "Active" : "Offline"}</span>
        </div>
        <p className="user-inbox-component-message-text">
          {!isLoading && data?.lastMessage ? `${lastMessageLabel}: ${data.lastMessage}` : "Open conversation"}
        </p>
      </div>
    </div>
  );
};

const SellerInbox = ({
  scrollRef,
  newMessage,
  setNewMessage,
  sendMessageHandler,
  messages,
  sellerId,
  userData,
  activeStatus,
  handleImageUpload,
}) => {
  return (
    <div className="user-inbox-component-chat-container shop-dashboard-inbox-thread">
      <div className="user-inbox-component-chat-header shop-dashboard-inbox-thread-header">
        <div className="user-inbox-component-chat-header-left">
          <img
            src={userData?.avatar?.url || "/DefaultAvatar.png"}
            alt={userData?.name || "Contact"}
            className="user-inbox-component-chat-avatar"
          />
          <div className="user-inbox-component-chat-user-info">
            <h1 className="user-inbox-component-chat-username">{userData?.name || "Contact"}</h1>
            <h1 className="user-inbox-component-chat-status">{activeStatus ? "Active Now" : "Offline"}</h1>
          </div>
        </div>
      </div>

      <div className="user-inbox-component-messages shop-dashboard-inbox-thread-messages">
        {messages && messages.length > 0 ? (
          messages.map((item, index) => {
            const messageImageSrc = getMessageImageSrc(item.images);

            return (
              <div
                className={`user-inbox-component-message ${item.sender === sellerId ? "sent" : "received"}`}
                key={item._id || index}
                ref={index === messages.length - 1 ? scrollRef : null}
              >
                {item.sender !== sellerId && (
                  <img
                    src={userData?.avatar?.url || "/DefaultAvatar.png"}
                    className="user-inbox-component-message-avatar"
                    alt={userData?.name || "Contact"}
                  />
                )}
                {messageImageSrc && (
                  <img
                    src={messageImageSrc}
                    className="user-inbox-component-message-image"
                    alt=""
                  />
                )}
                {item.text !== "" && (
                  <div className="user-inbox-component-message-bubble shop-dashboard-inbox-bubble">
                    <p>{item.text}</p>
                  </div>
                )}
                <p className="user-inbox-component-message-time">
                  {format(item.createdAt)}
                </p>
              </div>
            );
          })
        ) : (
          <div className="user-inbox-component-thread-empty user-inbox-component-thread-empty--compact">
            <p>Conversation</p>
            <h3>No messages yet</h3>
            <span>Send the first message or share an image to start the thread.</span>
          </div>
        )}
      </div>

      <form className="user-inbox-component-message-input-container shop-dashboard-inbox-compose" onSubmit={sendMessageHandler}>
        <div className="user-inbox-component-gallery-icon">
          <input
            type="file"
            id="shop-message-image"
            className="user-inbox-component-file-input"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            onChange={handleImageUpload}
          />
          <label htmlFor="shop-message-image">
            <TfiGallery size={20} />
          </label>
        </div>
        <div className="user-inbox-component-message-input-box">
          <input
            type="text"
            required
            placeholder="Enter your message..."
            value={newMessage}
            onChange={(event) => setNewMessage(event.target.value)}
            className="user-inbox-component-message-input"
          />
          <input type="submit" value="Send" className="user-inbox-component-send-button" id="shop-message-send" />
          <label htmlFor="shop-message-send">
            <AiOutlineSend size={20} className="user-inbox-component-send-icon" />
          </label>
        </div>
      </form>
    </div>
  );
};

export default DashboardMessages;
